import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { ThemeContext } from "../../../context/Theme/ThemeContext";
import { ROLES } from "../../../constants/roles";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import MasterPageBreadcrumb from "../../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../../layout/master/masterRouteMeta";
import MasterStatCard from "../../../components/Master/MasterStatCard";
import MasterSpinner from "../../../components/Master/MasterSpinner";

const PAGE_SIZE = 25;
const TABS = ["purchases", "subscriptions", "coupons"];

function formatMoney(value) {
	const n = Number(value);
	if (Number.isNaN(n)) return "—";
	return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDateTime(value) {
	if (!value) return "—";
	return new Date(value).toLocaleString("pt-BR", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function formatDate(value) {
	if (!value) return "—";
	return new Date(value).toLocaleDateString("pt-BR");
}

function formatDiscount(type, value) {
	const n = Number(value);
	if (Number.isNaN(n)) return "—";
	if (type === "PERCENTAGE") return `${n}%`;
	return formatMoney(n);
}

function paymentBadgeClass(status) {
	if (status === "PAID") return "master-badge master-badge--success";
	if (status === "PENDING") return "master-badge master-badge--warning";
	return "master-badge master-badge--danger";
}

function subBadgeClass(status) {
	if (status === "ACTIVE") return "master-badge master-badge--success";
	if (status === "EXPIRED") return "master-badge master-badge--warning";
	return "master-badge master-badge--danger";
}

function IconMoney() {
	return (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
		</svg>
	);
}

function IconClock() {
	return (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<polyline points="12 6 12 12 16 14" />
		</svg>
	);
}

function IconCheck() {
	return (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
			<polyline points="22 4 12 14.01 9 11.01" />
		</svg>
	);
}

function IconTag() {
	return (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
			<line x1="7" y1="7" x2="7.01" y2="7" />
		</svg>
	);
}

function periodPresetDates(preset) {
	const today = new Date();
	const to = today.toISOString().slice(0, 10);
	if (preset === "all") return { from: "", to: "" };
	if (preset === "month") {
		const from = new Date(today.getFullYear(), today.getMonth(), 1);
		return { from: from.toISOString().slice(0, 10), to };
	}
	if (preset === "90") {
		const from = new Date(today);
		from.setDate(from.getDate() - 90);
		return { from: from.toISOString().slice(0, 10), to };
	}
	const from = new Date(today);
	from.setDate(from.getDate() - 30);
	return { from: from.toISOString().slice(0, 10), to };
}

export default function MasterFinance() {
	const history = useHistory();
	const { user, loading: authLoading } = useContext(AuthContext);
	const { darkMode } = useContext(ThemeContext);
	const routeMeta = getMasterRouteMeta("/admin-master/finance");

	const [preset, setPreset] = useState("30");
	const [from, setFrom] = useState(() => periodPresetDates("30").from);
	const [to, setTo] = useState(() => periodPresetDates("30").to);
	const [tab, setTab] = useState("purchases");

	const [summary, setSummary] = useState(null);
	const [loadingSummary, setLoadingSummary] = useState(true);

	const [purchases, setPurchases] = useState([]);
	const [purchasesCount, setPurchasesCount] = useState(0);
	const [purchasesPage, setPurchasesPage] = useState(1);
	const [purchasesStatus, setPurchasesStatus] = useState("");
	const [loadingPurchases, setLoadingPurchases] = useState(false);

	const [subscriptions, setSubscriptions] = useState([]);
	const [subsCount, setSubsCount] = useState(0);
	const [subsPage, setSubsPage] = useState(1);
	const [subsStatus, setSubsStatus] = useState("");
	const [withCoupon, setWithCoupon] = useState("");
	const [loadingSubs, setLoadingSubs] = useState(false);

	const [couponUsage, setCouponUsage] = useState([]);
	const [loadingCoupons, setLoadingCoupons] = useState(false);

	const chartText = darkMode ? "#a1a1aa" : "#71717a";
	const chartGrid = darkMode ? "#3f3f46" : "#e4e4e7";

	const periodParams = useMemo(() => {
		const params = {};
		if (from) params.from = from;
		if (to) params.to = to;
		return params;
	}, [from, to]);

	useEffect(() => {
		if (authLoading) return;
		if (!user || user.profile !== ROLES.master) {
			history.replace("/");
		}
	}, [user, authLoading, history]);

	const fetchSummary = useCallback(() => {
		setLoadingSummary(true);
		return api
			.get("/master/finance/summary", { params: periodParams })
			.then(({ data }) => setSummary(data))
			.catch((err) => toastError(err))
			.finally(() => setLoadingSummary(false));
	}, [periodParams]);

	const fetchPurchases = useCallback(() => {
		setLoadingPurchases(true);
		const params = {
			...periodParams,
			pageNumber: String(purchasesPage),
		};
		if (purchasesStatus) params.paymentStatus = purchasesStatus;
		return api
			.get("/master/finance/purchases", { params })
			.then(({ data }) => {
				setPurchases(data.purchases || []);
				setPurchasesCount(data.count || 0);
			})
			.catch((err) => toastError(err))
			.finally(() => setLoadingPurchases(false));
	}, [periodParams, purchasesPage, purchasesStatus]);

	const fetchSubscriptions = useCallback(() => {
		setLoadingSubs(true);
		const params = {
			...periodParams,
			pageNumber: String(subsPage),
		};
		if (subsStatus) params.status = subsStatus;
		if (withCoupon) params.withCoupon = withCoupon;
		return api
			.get("/master/finance/subscriptions", { params })
			.then(({ data }) => {
				setSubscriptions(data.subscriptions || []);
				setSubsCount(data.count || 0);
			})
			.catch((err) => toastError(err))
			.finally(() => setLoadingSubs(false));
	}, [periodParams, subsPage, subsStatus, withCoupon]);

	const fetchCouponsUsage = useCallback(() => {
		setLoadingCoupons(true);
		return api
			.get("/master/finance/coupons-usage", { params: periodParams })
			.then(({ data }) => setCouponUsage(data.items || []))
			.catch((err) => toastError(err))
			.finally(() => setLoadingCoupons(false));
	}, [periodParams]);

	useEffect(() => {
		if (authLoading || !user || user.profile !== ROLES.master) return;
		fetchSummary();
	}, [authLoading, user, fetchSummary]);

	useEffect(() => {
		if (authLoading || !user || user.profile !== ROLES.master) return;
		if (tab === "purchases") fetchPurchases();
		if (tab === "subscriptions") fetchSubscriptions();
		if (tab === "coupons") fetchCouponsUsage();
	}, [
		authLoading,
		user,
		tab,
		fetchPurchases,
		fetchSubscriptions,
		fetchCouponsUsage,
	]);

	const applyPreset = (value) => {
		setPreset(value);
		if (value === "custom") return;
		const dates = periodPresetDates(value);
		setFrom(dates.from);
		setTo(dates.to);
		setPurchasesPage(1);
		setSubsPage(1);
	};

	const purchasesPages = Math.max(1, Math.ceil(purchasesCount / PAGE_SIZE));
	const subsPages = Math.max(1, Math.ceil(subsCount / PAGE_SIZE));

	if (authLoading || !user || user.profile !== ROLES.master) {
		return null;
	}

	return (
		<div className="mx-auto w-full max-w-none box-border">
			<MasterPageBreadcrumb
				title={i18n.t(routeMeta.titleKey)}
				breadcrumbs={routeMeta.breadcrumbs}
			/>
			{routeMeta.subtitleKey && (
				<p className="-mt-2 mb-6 text-sm text-default-500 dark:text-default-400">
					{i18n.t(routeMeta.subtitleKey)}
				</p>
			)}

			<div className="card mb-5">
				<div className="card-body">
					<div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
						<select
							className="form-select form-input-sm min-w-[160px] max-w-full sm:max-w-[200px]"
							value={preset}
							onChange={(e) => applyPreset(e.target.value)}
							aria-label={i18n.t("master.finance.periodPreset")}
						>
							<option value="30">{i18n.t("master.finance.period30")}</option>
							<option value="90">{i18n.t("master.finance.period90")}</option>
							<option value="month">{i18n.t("master.finance.periodMonth")}</option>
							<option value="all">{i18n.t("master.finance.periodAll")}</option>
							<option value="custom">{i18n.t("master.finance.periodCustom")}</option>
						</select>
						<input
							type="date"
							className="form-input form-input-sm max-w-full sm:!w-[10rem]"
							value={from}
							onChange={(e) => {
								setPreset("custom");
								setFrom(e.target.value);
								setPurchasesPage(1);
								setSubsPage(1);
							}}
							aria-label={i18n.t("master.finance.from")}
						/>
						<input
							type="date"
							className="form-input form-input-sm max-w-full sm:!w-[10rem]"
							value={to}
							onChange={(e) => {
								setPreset("custom");
								setTo(e.target.value);
								setPurchasesPage(1);
								setSubsPage(1);
							}}
							aria-label={i18n.t("master.finance.to")}
						/>
					</div>
				</div>
			</div>

			{loadingSummary ? (
				<div className="mb-6 flex flex-col items-center justify-center gap-4 py-10 text-default-500">
					<MasterSpinner />
				</div>
			) : (
				<>
					<div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
						<MasterStatCard
							tone="success"
							icon={<IconMoney />}
							value={formatMoney(summary?.revenuePaid)}
							label={i18n.t("master.finance.kpiRevenuePaid")}
						/>
						<MasterStatCard
							tone="secondary"
							icon={<IconClock />}
							value={formatMoney(summary?.revenuePending)}
							label={i18n.t("master.finance.kpiRevenuePending")}
						/>
						<MasterStatCard
							tone="primary"
							icon={<IconCheck />}
							value={summary?.subscriptionsByStatus?.ACTIVE ?? 0}
							label={i18n.t("master.finance.kpiSubsActive")}
						/>
						<MasterStatCard
							tone="info"
							icon={<IconTag />}
							value={summary?.subscriptionsWithCoupon ?? 0}
							label={i18n.t("master.finance.kpiSubsWithCoupon")}
						/>
					</div>

					<div className="mb-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
						<div className="card lg:col-span-2">
							<div className="card-header">
								<h6 className="card-title m-0">
									{i18n.t("master.finance.revenueChart")}
								</h6>
							</div>
							<div className="card-body h-64">
								{(summary?.revenueByMonth || []).every((x) => !x.total) ? (
									<p className="m-0 text-sm text-default-500">
										{i18n.t("master.finance.noChartData")}
									</p>
								) : (
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={summary?.revenueByMonth || []}>
											<CartesianGrid stroke={chartGrid} strokeDasharray="3 3" />
											<XAxis dataKey="month" stroke={chartText} fontSize={12} />
											<YAxis stroke={chartText} fontSize={12} />
											<Tooltip
												formatter={(v) => formatMoney(v)}
												contentStyle={{
													background: darkMode ? "#18181b" : "#fff",
													borderColor: chartGrid,
												}}
											/>
											<Bar dataKey="total" fill="#22c55e" radius={[4, 4, 0, 0]} />
										</BarChart>
									</ResponsiveContainer>
								)}
							</div>
						</div>
						<div className="card">
							<div className="card-header">
								<h6 className="card-title m-0">
									{i18n.t("master.finance.purchasesByStatus")}
								</h6>
							</div>
							<div className="card-body space-y-3">
								<div className="flex justify-between text-sm">
									<span className="text-default-600 dark:text-default-300">
										{i18n.t("master.finance.statusPaid")}
									</span>
									<strong>{summary?.purchasesByStatus?.PAID ?? 0}</strong>
								</div>
								<div className="flex justify-between text-sm">
									<span className="text-default-600 dark:text-default-300">
										{i18n.t("master.finance.statusPending")}
									</span>
									<strong>{summary?.purchasesByStatus?.PENDING ?? 0}</strong>
								</div>
								<div className="flex justify-between text-sm">
									<span className="text-default-600 dark:text-default-300">
										{i18n.t("master.finance.statusCanceled")}
									</span>
									<strong>{summary?.purchasesByStatus?.CANCELED ?? 0}</strong>
								</div>
								<hr className="border-default-200 dark:border-default-700" />
								<div className="flex justify-between text-sm">
									<span className="text-default-600 dark:text-default-300">
										{i18n.t("master.finance.subsCanceled")}
									</span>
									<strong>{summary?.subscriptionsByStatus?.CANCELED ?? 0}</strong>
								</div>
								<div className="flex justify-between text-sm">
									<span className="text-default-600 dark:text-default-300">
										{i18n.t("master.finance.subsExpired")}
									</span>
									<strong>{summary?.subscriptionsByStatus?.EXPIRED ?? 0}</strong>
								</div>
							</div>
						</div>
					</div>
				</>
			)}

			<div className="card">
				<div className="card-header">
					<div className="flex flex-wrap items-center gap-2">
						{TABS.map((key) => (
							<button
								key={key}
								type="button"
								className={`btn btn-sm ${
									tab === key ? "btn-primary" : "btn-sub-gray"
								}`}
								onClick={() => setTab(key)}
							>
								{i18n.t(`master.finance.tab.${key}`)}
							</button>
						))}
					</div>
				</div>

				{(tab === "purchases" || tab === "subscriptions") && (
					<div className="card-body border-b border-default-200 pb-5 dark:border-default-700">
						<div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
							{tab === "purchases" && (
								<select
									className="form-select form-input-sm min-w-[160px] max-w-full sm:max-w-[200px]"
									value={purchasesStatus}
									onChange={(e) => {
										setPurchasesStatus(e.target.value);
										setPurchasesPage(1);
									}}
								>
									<option value="">{i18n.t("master.finance.allStatuses")}</option>
									<option value="PAID">{i18n.t("master.finance.statusPaid")}</option>
									<option value="PENDING">{i18n.t("master.finance.statusPending")}</option>
									<option value="CANCELED">
										{i18n.t("master.finance.statusCanceled")}
									</option>
								</select>
							)}
							{tab === "subscriptions" && (
								<>
									<select
										className="form-select form-input-sm min-w-[160px] max-w-full sm:max-w-[200px]"
										value={subsStatus}
										onChange={(e) => {
											setSubsStatus(e.target.value);
											setSubsPage(1);
										}}
									>
										<option value="">{i18n.t("master.finance.allStatuses")}</option>
										<option value="ACTIVE">{i18n.t("master.finance.statusActive")}</option>
										<option value="CANCELED">
											{i18n.t("master.finance.statusCanceled")}
										</option>
										<option value="EXPIRED">
											{i18n.t("master.finance.statusExpired")}
										</option>
									</select>
									<select
										className="form-select form-input-sm min-w-[160px] max-w-full sm:max-w-[220px]"
										value={withCoupon}
										onChange={(e) => {
											setWithCoupon(e.target.value);
											setSubsPage(1);
										}}
									>
										<option value="">{i18n.t("master.finance.allCoupons")}</option>
										<option value="true">{i18n.t("master.finance.onlyWithCoupon")}</option>
									</select>
								</>
							)}
						</div>
					</div>
				)}

				{tab === "purchases" && (
					<>
						{loadingPurchases ? (
							<div className="flex flex-col items-center justify-center gap-4 px-6 py-10 text-default-500">
								<MasterSpinner />
							</div>
						) : purchases.length === 0 ? (
							<div className="px-6 py-10 text-center text-sm text-default-500">
								{i18n.t("master.finance.emptyPurchases")}
							</div>
						) : (
							<div className="overflow-x-auto">
								<table className="master-table w-full">
									<thead>
										<tr>
											<th>{i18n.t("master.finance.colCompany")}</th>
											<th>{i18n.t("master.finance.colAddon")}</th>
											<th>{i18n.t("master.finance.colTotal")}</th>
											<th>{i18n.t("master.finance.colDiscount")}</th>
											<th>{i18n.t("master.finance.colStatus")}</th>
											<th>{i18n.t("master.finance.colPaymentId")}</th>
											<th>{i18n.t("master.finance.colDate")}</th>
										</tr>
									</thead>
									<tbody>
										{purchases.map((row) => (
											<tr key={row.id}>
												<td>{row.companyName || `#${row.companyId}`}</td>
												<td>{row.addonName || "—"}</td>
												<td>{formatMoney(row.totalPrice)}</td>
												<td>{formatMoney(row.discountApplied)}</td>
												<td>
													<span className={paymentBadgeClass(row.paymentStatus)}>
														{i18n.t(
															`master.finance.status${row.paymentStatus === "PAID" ? "Paid" : row.paymentStatus === "PENDING" ? "Pending" : "Canceled"}`
														)}
													</span>
												</td>
												<td className="font-mono text-xs">{row.paymentId || "—"}</td>
												<td>{formatDateTime(row.createdAt)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
						{purchasesCount > PAGE_SIZE && (
							<div className="card-footer flex items-center justify-between gap-3">
								<span className="text-sm text-default-500">
									{purchasesCount} {i18n.t("master.finance.records")}
								</span>
								<div className="flex items-center gap-2">
									<button
										type="button"
										className="btn btn-sm btn-sub-gray"
										disabled={purchasesPage <= 1}
										onClick={() => setPurchasesPage((p) => Math.max(1, p - 1))}
									>
										{i18n.t("master.finance.prev")}
									</button>
									<span className="text-sm text-default-600 dark:text-default-300">
										{purchasesPage}/{purchasesPages}
									</span>
									<button
										type="button"
										className="btn btn-sm btn-sub-gray"
										disabled={purchasesPage >= purchasesPages}
										onClick={() =>
											setPurchasesPage((p) => Math.min(purchasesPages, p + 1))
										}
									>
										{i18n.t("master.finance.next")}
									</button>
								</div>
							</div>
						)}
					</>
				)}

				{tab === "subscriptions" && (
					<>
						{loadingSubs ? (
							<div className="flex flex-col items-center justify-center gap-4 px-6 py-10 text-default-500">
								<MasterSpinner />
							</div>
						) : subscriptions.length === 0 ? (
							<div className="px-6 py-10 text-center text-sm text-default-500">
								{i18n.t("master.finance.emptySubscriptions")}
							</div>
						) : (
							<div className="overflow-x-auto">
								<table className="master-table w-full">
									<thead>
										<tr>
											<th>{i18n.t("master.finance.colCompany")}</th>
											<th>{i18n.t("master.finance.colPlan")}</th>
											<th>{i18n.t("master.finance.colStatus")}</th>
											<th>{i18n.t("master.finance.colTokens")}</th>
											<th>{i18n.t("master.finance.colCoupon")}</th>
											<th>{i18n.t("master.finance.colStarted")}</th>
											<th>{i18n.t("master.finance.colExpires")}</th>
										</tr>
									</thead>
									<tbody>
										{subscriptions.map((row) => (
											<tr key={row.id}>
												<td>{row.companyName || `#${row.companyId}`}</td>
												<td>{row.planName || "—"}</td>
												<td>
													<span className={subBadgeClass(row.status)}>
														{i18n.t(
															`master.finance.status${
																row.status === "ACTIVE"
																	? "Active"
																	: row.status === "EXPIRED"
																		? "Expired"
																		: "Canceled"
															}`
														)}
													</span>
												</td>
												<td>
													{row.usedTokens}/{row.monthlyTokens + row.extraTokens}
												</td>
												<td>{row.couponName || "—"}</td>
												<td>{formatDate(row.startedAt)}</td>
												<td>{formatDate(row.expiresAt)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
						{subsCount > PAGE_SIZE && (
							<div className="card-footer flex items-center justify-between gap-3">
								<span className="text-sm text-default-500">
									{subsCount} {i18n.t("master.finance.records")}
								</span>
								<div className="flex items-center gap-2">
									<button
										type="button"
										className="btn btn-sm btn-sub-gray"
										disabled={subsPage <= 1}
										onClick={() => setSubsPage((p) => Math.max(1, p - 1))}
									>
										{i18n.t("master.finance.prev")}
									</button>
									<span className="text-sm text-default-600 dark:text-default-300">
										{subsPage}/{subsPages}
									</span>
									<button
										type="button"
										className="btn btn-sm btn-sub-gray"
										disabled={subsPage >= subsPages}
										onClick={() => setSubsPage((p) => Math.min(subsPages, p + 1))}
									>
										{i18n.t("master.finance.next")}
									</button>
								</div>
							</div>
						)}
					</>
				)}

				{tab === "coupons" && (
					<>
						{loadingCoupons ? (
							<div className="flex flex-col items-center justify-center gap-4 px-6 py-10 text-default-500">
								<MasterSpinner />
							</div>
						) : couponUsage.length === 0 ? (
							<div className="px-6 py-10 text-center text-sm text-default-500">
								{i18n.t("master.finance.emptyCoupons")}
							</div>
						) : (
							<div className="overflow-x-auto">
								<table className="master-table w-full">
									<thead>
										<tr>
											<th>{i18n.t("master.finance.colCoupon")}</th>
											<th>{i18n.t("master.finance.colDiscount")}</th>
											<th>{i18n.t("master.finance.colUsage")}</th>
											<th>{i18n.t("master.finance.colLastUsed")}</th>
											<th>{i18n.t("master.finance.colStatus")}</th>
										</tr>
									</thead>
									<tbody>
										{couponUsage.map((row) => (
											<tr key={row.couponId}>
												<td>{row.name}</td>
												<td>{formatDiscount(row.discountType, row.discountValue)}</td>
												<td>{row.usageCount}</td>
												<td>{formatDateTime(row.lastUsedAt)}</td>
												<td>
													<span
														className={
															row.active
																? "master-badge master-badge--success"
																: "master-badge master-badge--danger"
														}
													>
														{row.active
															? i18n.t("master.finance.statusActive")
															: i18n.t("master.finance.statusInactive")}
													</span>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}
